/**
 * KL24 cookie consent banner – Shopify Customer Privacy API + localStorage fallback.
 */
(() => {
  const STORAGE_KEY = 'kl24_cookie_consent';
  const banner = document.getElementById('kl24-cookie-banner');
  if (!banner) return;

  let consentHandledByUser = false;

  const hideShopifyBanner = () => {
    document
      .querySelectorAll(
        '#shopify-pc__banner, #shopify-pc__prefs__dialog, shopify-privacy-banner, .shopify-pc__banner__dialog'
      )
      .forEach((el) => {
        if (el instanceof HTMLElement) {
          el.style.setProperty('display', 'none', 'important');
          el.style.setProperty('visibility', 'hidden', 'important');
          el.style.setProperty('pointer-events', 'none', 'important');
        }
      });
  };

  const hideBanner = () => {
    banner.hidden = true;
    banner.classList.remove('is-open');
    banner.setAttribute('aria-hidden', 'true');
  };

  const showBanner = () => {
    banner.hidden = false;
    banner.classList.add('is-open');
    banner.removeAttribute('aria-hidden');
  };

  const persistConsent = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* ignore */
    }
  };

  const readStoredConsent = () => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  };

  const shopifyConsent = () => {
    try {
      return window.Shopify?.customerPrivacy?.currentVisitorConsent?.() || null;
    } catch {
      return null;
    }
  };

  const consentDecided = () => {
    const stored = readStoredConsent();
    if (stored === 'accepted' || stored === 'rejected') return true;
    const current = shopifyConsent();
    if (current?.analytics === 'yes' || current?.analytics === 'no') return true;
    return false;
  };

  const applyChoice = (accepted) => {
    if (accepted) {
      window.Kl24Ga4?.grantAnalytics?.();
    } else {
      window.Kl24Ga4?.denyAnalytics?.();
    }
  };

  const setShopifyConsent = (accepted, callback) => {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy?.setTrackingConsent) {
      callback?.();
      return;
    }

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      callback?.();
    };

    try {
      privacy.setTrackingConsent(
        {
          analytics: accepted,
          marketing: false,
          preferences: false,
          sale_of_data: false,
        },
        finish
      );
      window.setTimeout(finish, 1500);
    } catch {
      finish();
    }
  };

  const handleChoice = (accepted) => {
    consentHandledByUser = true;
    persistConsent(accepted ? 'accepted' : 'rejected');
    applyChoice(accepted);
    hideBanner();

    banner.querySelectorAll('[data-kl24-consent]').forEach((button) => {
      if (button instanceof HTMLButtonElement) {
        button.disabled = true;
      }
    });

    setShopifyConsent(accepted, () => {
      applyChoice(accepted);
    });
  };

  const syncFromExisting = () => {
    const stored = readStoredConsent();
    if (stored === 'accepted') {
      consentHandledByUser = true;
      applyChoice(true);
      hideBanner();
      return true;
    }
    if (stored === 'rejected') {
      consentHandledByUser = true;
      applyChoice(false);
      hideBanner();
      return true;
    }

    const current = shopifyConsent();
    if (current?.analytics === 'yes') {
      consentHandledByUser = true;
      persistConsent('accepted');
      applyChoice(true);
      hideBanner();
      return true;
    }
    if (current?.analytics === 'no') {
      consentHandledByUser = true;
      persistConsent('rejected');
      applyChoice(false);
      hideBanner();
      return true;
    }
    return false;
  };

  const init = () => {
    if (syncFromExisting()) return;
    showBanner();
  };

  banner.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('[data-kl24-consent]');
      if (!button || !banner.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      const accepted = button.getAttribute('data-kl24-consent') === 'accept';
      handleChoice(accepted);
    },
    true
  );

  document.addEventListener('visitorConsentCollected', (event) => {
    if (consentHandledByUser) return;
    const detail = event.detail || {};
    const accepted = detail.analyticsAllowed === true || detail.analytics === 'yes';
    persistConsent(accepted ? 'accepted' : 'rejected');
    applyChoice(accepted);
    hideBanner();
  });

  const boot = () => {
    hideShopifyBanner();
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(hideShopifyBanner);
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }

    if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => {
          if (!error) init();
          else if (!consentDecided()) showBanner();
        }
      );
      return;
    }
    init();
  };

  const openCookieSettings = () => {
    banner.querySelectorAll('[data-kl24-consent]').forEach((button) => {
      if (button instanceof HTMLButtonElement) {
        button.disabled = false;
      }
    });
    showBanner();
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('a[href="#cookie-einstellungen"], a[href$="#cookie-einstellungen"]');
    if (!trigger) return;
    event.preventDefault();
    openCookieSettings();
  });

  window.addEventListener('hashchange', () => {
    if (window.location.hash === '#cookie-einstellungen') {
      openCookieSettings();
    }
  });

  if (window.location.hash === '#cookie-einstellungen') {
    openCookieSettings();
  }

  window.Kl24CookieConsent = {
    openSettings: openCookieSettings,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
